import { type Dictionary } from '@ngrx/entity';
import { type PartialDeep } from 'type-fest';

import type {
  CanvasElement,
  CanvasElementFrom,
  CanvasElementId,
  ColumnId,
  FormContainerVariantType,
} from '@blaze-runtime/engine-domain';
import { WrapperField } from '@blaze-runtime/engine-domain';
import {
  type CanvasElementPropertiesWithData,
  type FontOptions,
  getActiveBinding,
  type PropertiesConfigInputColorModel,
} from '@blaze-shared/elements-domain';
import { type SchemaService } from '@blaze-shared/schema-data-access';

import {
  hasDataProps,
  hasWrapperRefs,
} from '../../canvas-elements/element-model/element-props-guards';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { type ParentChildrenOverrides } from '../../canvas-elements/parent-overrides';

import {
  WIZARD_STEPS_ITEMS_PATH,
  type WizardPropsModel,
  type WizardStep,
  type WizardTextStyleProps,
} from './wizard.models';

/**
 * The role a generated Text element plays for the wizard that styles it, naming which of the
 * wizard's children-override bundles applies. Declared here rather than shared across the
 * styling seam: the roles are the wizard's own vocabulary, and no other parent's can leak in.
 */
export type WizardTextStyleGroup = 'question' | 'explanation';

type WizardProps = Pick<WizardPropsModel, 'content'> | undefined;

/**
 * A wizard's props as its `childrenOverridesResolver` receives them - merged for the render
 * target, but not filled out with defaults, so every field down the path may be absent.
 */
type RawWizardProps = PartialDeep<Pick<WizardPropsModel, 'content'>> | undefined;

const TEXT_STYLE_GROUPS = [
  'question',
  'explanation',
] as const satisfies readonly WizardTextStyleGroup[];

type WizardConfirmationPostAction = NonNullable<WizardStep['postAction']>;

/** All {@link resolveWizardDatasetId} needs of the schema service - it knows no wizards. */
type DatasetIdResolver = Pick<SchemaService, 'getDatasetId'>;

/**
 * The dataset a wizard is bound to, resolved from its own `value` binding.
 *
 * Both wizard modes share that binding, and it never holds a dataset id directly: picking a
 * table generates an element input workflow and binds *that*, so the id it carries is a
 * workflow's while everything downstream - schema lookups, wrapper refs, the questionnaire
 * generation - is keyed by dataset. This resolution is what the Add Question pane and the
 * generation core must agree on; resolving it twice is how they came to offer columns from one
 * schema and generate from another.
 *
 * Undefined while nothing is bound, or while the bound workflow reads from no table.
 *
 * Takes the service rather than injecting it: the pane calls this from a knob resolver and the
 * generation core from a plain service method, which is not an injection context.
 */
export function resolveWizardDatasetId(
  resolver: DatasetIdResolver,
  props: CanvasElementPropertiesWithData | undefined,
): string | undefined {
  const dataSource = getActiveBinding(props?.data, 'value')?.dataSource;
  return dataSource && resolver.getDatasetId(dataSource.id, dataSource.sourceType);
}

export function isWizardQuestionnaireMode(props: WizardProps): boolean {
  return (props?.content.mode ?? 'form') === 'questionnaire';
}

/**
 * Null-safe read of the steps list, shared by both wizard modes - a questionnaire question
 * is a step with a `columnKey`/`question`. Skips empty entries left by partial props patches.
 */
export function getWizardSteps(props: WizardProps): WizardStep[] {
  return (props?.content.steps?.items ?? []).filter((step): step is WizardStep => !!step);
}

/**
 * A questionnaire that has no questions yet - the confirmation step every wizard carries is
 * its only one. Nothing of it is renderable: a confirmation is drawn from its own props and
 * its slot's contents never show, so the canvas has nothing to put on screen.
 */
export function isQuestionlessQuestionnaire(props: WizardProps): boolean {
  return (
    isWizardQuestionnaireMode(props) && !getWizardSteps(props).some(({ columnKey }) => !!columnKey)
  );
}

/**
 * Parses a step sub-property props path (`content.steps.items.<n>.<field>`) into the step
 * index and field name; undefined for any other path, including the whole-list path itself.
 */
export function parseWizardStepFieldPath(
  path: string,
): { stepIndex: number; field: keyof WizardStep } | undefined {
  const prefix = `${WIZARD_STEPS_ITEMS_PATH}.`;
  if (!path.startsWith(prefix)) {
    return undefined;
  }
  const segments = path.slice(prefix.length).split('.');
  const stepIndex = Number(segments[0]);
  if (
    segments.length !== 2 ||
    String(stepIndex) !== segments[0] ||
    !Number.isInteger(stepIndex) ||
    stepIndex < 0
  ) {
    return undefined;
  }
  return { stepIndex, field: segments[1] as keyof WizardStep };
}

export function getWizardConfirmationPostAction(
  props: WizardProps,
): WizardConfirmationPostAction | undefined {
  // question steps have no postAction - the optional read just returns undefined for them
  return getWizardSteps(props).find((step) => step.type === 'confirmation')?.postAction;
}

/** Step N always maps to slot N, so the step's index is also its slot's index. */
export function getWizardConfirmationSlotIndex(props: WizardProps): number | undefined {
  const confirmationIndex = getWizardSteps(props).findIndex((step) => step.type === 'confirmation');
  return confirmationIndex >= 0 ? confirmationIndex : undefined;
}

export function getWizardConfirmationSlotId(
  props: WizardProps,
  slotIds: ColumnId[] | undefined,
): ColumnId | undefined {
  const slotIndex = getWizardConfirmationSlotIndex(props);
  return slotIndex !== undefined ? slotIds?.[slotIndex] : undefined;
}

export function getWizardStepLabelBySlotIndex(
  props: WizardProps,
  slotIndex: number,
): string | undefined {
  return getWizardSteps(props)[slotIndex]?.label;
}

/** Progress counts the current step too, so the last step shows 100% before submit. */
export function getWizardProgressPercent(selectedIndex: number, stepsCount: number): number {
  if (stepsCount <= 0) {
    return 0;
  }
  return Math.round(((selectedIndex + 1) / stepsCount) * 100);
}

export function isWizardReviewSlot(props: WizardProps, slotIndex: number): boolean {
  if (!props || isWizardQuestionnaireMode(props)) {
    return false;
  }
  return props.content.steps?.items[slotIndex]?.type === 'review';
}

/**
 * Children overrides bundle for the questionnaire's question/explanation Text elements,
 * resolved against the wizard definition defaults - raw wizard props may not carry
 * style.question/style.explanation yet.
 */
export function resolveWizardTextStyleOverrides(
  defaults: WizardTextStyleProps | undefined,
  raw: PartialDeep<WizardTextStyleProps> | undefined,
): ParentChildrenOverrides {
  return {
    style: {
      text: {
        font: { ...defaults?.font, ...raw?.font } as Partial<FontOptions>,
        textColor: (raw?.textColor ?? defaults?.textColor) as
          | PropertiesConfigInputColorModel
          | undefined,
      },
    },
  };
}

/**
 * Ids of the generated Text elements of the given role, read from the persisted step ids -
 * the wizard's analog of a form's wrapper refs.
 */
export function getWizardQuestionnaireTextElementIds(
  props: WizardProps,
  group: WizardTextStyleGroup,
): CanvasElementId[] {
  return getWizardSteps(props)
    .map((step) => stepTextElementId(step, group))
    .filter((elementId): elementId is CanvasElementId => !!elementId);
}

/**
 * The role a given element plays for this wizard, found by asking the steps which of them
 * points at it - the ids {@link getWizardQuestionnaireTextElementIds} reads, walked the other
 * way. Undefined for anything the wizard did not generate, which is what keeps a pasted clone
 * of a question text from being styled by the wizard its copied marker still names.
 */
export function getWizardTextStyleGroup(
  props: RawWizardProps,
  elementId: CanvasElementId,
): WizardTextStyleGroup | undefined {
  const steps = props?.content?.steps?.items ?? [];

  return TEXT_STYLE_GROUPS.find((group) =>
    steps.some((step) => stepTextElementId(step, group) === elementId),
  );
}

/** Which Text element a step records for a role - the pairing both lookups above read. */
function stepTextElementId(
  step: PartialDeep<WizardStep> | undefined,
  group: WizardTextStyleGroup,
): CanvasElementId | undefined {
  return group === 'question' ? step?.questionElementId : step?.explanationElementId;
}

/**
 * The generated field element bound to the given question column inside a question's form.
 * Generation names the field's binding data source with the column key, so the match works
 * from props alone even when the builder added extra fields to the form.
 */
export function getWizardQuestionFieldOfForm(
  elements: Dictionary<CanvasElement>,
  form: CanvasElementFrom<FormContainerVariantType>,
  columnKey: string,
): CanvasElement | undefined {
  const refs = hasWrapperRefs(form) ? getWrapperRefs(form) : {};
  return Object.entries(refs)
    .filter(([refId]) => WrapperField.isRefId(refId))
    .map(([, fieldId]) => elements[fieldId])
    .find(
      (field) =>
        hasDataProps(field) &&
        (field.props.data ?? []).some((binding) => binding?.dataSource?.name === columnKey),
    );
}
